import Button from 'atoms/Button';
import Text from 'atoms/Text';
import DialogBox from 'molecules/DialogBox';
import FileUpload from 'molecules/FileUpload';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { toast } from 'react-toastify';

const ALLOWED_HELP_DOC_EXTS = ['.pdf'];
const MAX_FILE_BYTES = 25 * 1024 * 1024;

function getLowerExt(name = '') {
    const parts = String(name).split('.');
    return parts.length > 1 ? `.${parts.pop().toLowerCase()}` : '';
}

function defaultDownload(fileName = '') {
    if (!fileName) return;
    const filePath = `/${fileName}`;
    const link = document.createElement('a');
    link.href = filePath;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

function ManagedFileUploadDialog({
    show,
    title = 'Upload Document',
    templateFileName = '',
    uploadLabel,
    supportedText = 'Supported format: PDF. Max Size: 25MB',
    onClose,
    onContinue
}) {
    if (!show) return null;

    const [file, setFile] = useState(null);
    const [isError, setIsError] = useState(false);

    const close = () => {
        setIsError(false);
        setFile(null);
        onClose?.();
    };

    const continueClick = async () => {
        if (file && file[0]) {
            try {
                const ext = getLowerExt(file[0]?.name);
                if (!ALLOWED_HELP_DOC_EXTS.includes(ext)) {
                    setIsError(true);
                    throw new Error('Unsupported file type');
                }
                if (file[0]?.size > MAX_FILE_BYTES) {
                    toast('File size must be 25MB or less', { type: 'error' });
                    setIsError(true);
                    throw new Error('File too large');
                }
                await Promise.resolve(onContinue?.(file));
                close();
            } catch (_e) {
                setIsError(true);
            }
        } else {
            setIsError(true);
        }
    };

    return (
        <DialogBox
            title={title}
            titleStyle="text-center"
            width="w-full max-w-[448px]"
            close={close}
        >
            <FileUpload
                name="csv"
                iconSrc='/images/icons/upload-cloud.svg'
                iconWidth={50}
                variant="uploadDrag"
                accept=".pdf"
                onChange={(e) => {
                    const picked = e?.[0];
                    if (!picked) {
                        setFile(e);
                        setIsError(false);
                        return;
                    }

                    const ext = getLowerExt(picked?.name);
                    if (!ALLOWED_HELP_DOC_EXTS.includes(ext)) {
                        setFile(null);
                        setIsError(true);
                        return;
                    }

                    if (picked.size > MAX_FILE_BYTES) {
                        toast('File size must be 25MB or less', { type: 'error' });
                        setFile(null);
                        setIsError(true);
                        return;
                    }

                    setFile(e);
                    setIsError(false);
                }}
                isError={isError}
                deleteFile={() => setFile(null)}
                UploadLabel={
                    <>
                        Drop your file(s) here or <span className="text-primary-300 cursor-pointer">browse</span>
                    </>
                } UploadSubText={supportedText}
            />

            <div className="flex items-center gap-3 mt-9">
                <Button
                    label="Cancel"
                    type="ghost"
                    buttonTheme="gray"
                    className="flex-1 justify-center"
                    onClick={close}
                />
                <Button
                    label="Upload"
                    className="flex-1 justify-center"
                    onClick={continueClick}
                    disabled={!file || !file[0]}
                />
            </div>
        </DialogBox>
    );
}

ManagedFileUploadDialog.propTypes = {
    show: PropTypes.bool,
    title: PropTypes.string,
    templateFileName: PropTypes.string,
    uploadLabel: PropTypes.string,
    supportedText: PropTypes.string,
    onClose: PropTypes.func,
    onContinue: PropTypes.func
};

export default ManagedFileUploadDialog;

